import { useNavigate } from "react-router-dom";
import { useQuestionnaireQueryStates } from "../hooks/useQueryParams.ts";
import { QuestionnaireAnswers } from "../components/contact-form/utils/questionnaireStorage.ts";
import { captureMessage } from "@sentry/react";
import { NoSitesReason } from "../types/eligibility.ts";
import { useLandingPageConfig } from "../hooks/useLandingPageConfig.ts";

type QuestionnaireQueryState = ReturnType<
  typeof useQuestionnaireQueryStates
>[0] & { isAgeEligible?: boolean };

function determineNoTrialSitesMatchReason(
  isAgeEligible: boolean | undefined,
  prescreeningStatus: string | undefined,
): NoSitesReason {
  if (isAgeEligible === false) return NoSitesReason.AGE_INELIGIBLE;
  if (prescreeningStatus === "failed") return NoSitesReason.DISQUALIFIED;
  return NoSitesReason.GEOGRAPHIC;
}

function logNoTrialSitesMatchInSentry(
  reason: NoSitesReason,
  details: {
    landingPageId: string;
    patientId?: number | string | null;
    formData: any;
  },
) {
  const message =
    reason === NoSitesReason.AGE_INELIGIBLE
      ? "Patient ineligible due to age configuration"
      : "No trial sites available for patient";

  captureMessage(message, {
    level: "info",
    tags: {
      landing_page_id: details.landingPageId,
      error_type:
        reason === NoSitesReason.AGE_INELIGIBLE
          ? "age_ineligible"
          : "no_trial_sites",
      redirect_reason: reason,
    },
    extra: {
      patientId: details.patientId,
      formData: JSON.stringify(details.formData),
    },
  });
}

export function useNavigateToRedirectPage() {
  const navigate = useNavigate();
  const pageConfig = useLandingPageConfig();

  return function navigateToRedirectPage(
    questionnaireQueryState: QuestionnaireQueryState,
    questionnaireResults: QuestionnaireAnswers,
    landingPageId: string,
  ) {
    if (pageConfig?.thankYouPage) {
      navigate(`/${landingPageId}/thank-you`);
      return;
    }

    const { formData, apiResponse, isAgeEligible } = questionnaireQueryState;

    const hasMatchingTrialSites =
      apiResponse?.outcome.successfullyCreatedReferral &&
      !!apiResponse?.outcome.trialSiteDetails;

    if (hasMatchingTrialSites) {
      navigate(`/${landingPageId}/redirect`);
      return;
    }

    const reason = determineNoTrialSitesMatchReason(
      isAgeEligible,
      questionnaireResults.prescreeningStatus,
    );

    logNoTrialSitesMatchInSentry(reason, {
      landingPageId,
      patientId: apiResponse?.outcome.patientId,
      formData,
    });

    navigate(`/${landingPageId}/no-sites?reason=${reason}`);
  };
}
